// PDP add-to-cart gate + store-status card under the buy buttons.
// Enforces the store-first commitment rule: once a store is saved, every
// product is evaluated against it; not available there -> ATC says "Sold Out
// at [Store]" and submit is blocked. Items added in pickup mode get a hidden
// _fulfillment=pickup line property so the checkout validation function can
// distinguish pickup lines from ship lines.
//
// Signals: per-form [data-pickup-form-data] JSON (exempt/full flags + per-variant
// hqOos + per-store availability keyed by location id); localStorage
// s2_pickup_store {id,name}; /cart.js for mode (lines + pickup_store_name attr).
(function () {
  if (window.__pickupAtcGateInit) return;
  window.__pickupAtcGateInit = true;

  const log = (...a) => console.log("[pickup-atc-gate]", ...a);
  const FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  const SUBMIT_SELECTOR = ".product-form__submit";
  const LABEL_SELECTOR = ".add_to_cart_quantity";

  // Memoize the parse per [data-pickup-form-data] script ELEMENT. The JSON in a
  // given element never changes, but the element is replaced wholesale when Dawn
  // re-renders a section or quick-add.js clones a form — so keying on the element
  // (not the form) gives a replaced element a fresh parse and lets the old entry
  // GC out, no invalidation hook needed. refreshUi() hits getFormData ~5×/form and
  // runs on a 50ms debounce over ~40 grid forms, so this drops ~200 redundant
  // parses/tick. FAILED_PARSE (a sentinel, since a WeakMap can't store null
  // distinguishably via .get) caches the malformed-blob case too, so we parse and
  // console.error only once per element instead of every tick.
  const formDataCache = new WeakMap();
  const FAILED_PARSE = Symbol("pickup-form-data-parse-failed");
  function getFormData(form) {
    const el = form && form.querySelector("[data-pickup-form-data]");
    if (!el) return null;
    if (formDataCache.has(el)) {
      const cached = formDataCache.get(el);
      return cached === FAILED_PARSE ? null : cached;
    }
    try {
      const parsed = JSON.parse(el.textContent);
      formDataCache.set(el, parsed);
      return parsed;
    } catch (e) {
      console.error("[pickup-atc-gate] could not parse form data", e);
      formDataCache.set(el, FAILED_PARSE);
      return null;
    }
  }

  function getSelectedStore() {
    try {
      return JSON.parse(localStorage.getItem("s2_pickup_store") || "null");
    } catch {
      return null;
    }
  }

  const currentVariantId = (form) => form?.querySelector('[name="id"]')?.value || null;

  function variantData(form) {
    const data = getFormData(form);
    const vid = currentVariantId(form);
    if (!data || !data.variants || !vid) return null;
    return data.variants[vid] || null;
  }

  function isPickupOnly(form) {
    const vd = variantData(form);
    return !!(vd && vd.hqOos === true);
  }

  // Live per-variant availability, fetched from the same section endpoint the
  // store-selector uses (see fetchVariantStoreAvailability there — kept a local
  // twin to avoid a cross-file load-order dependency; Phase 5 shared-helper
  // backlog would dedupe them). fetchedAvail: variantId -> Map<locationId,bool>.
  const fetchedAvail = new Map();
  const availPromises = new Map();
  const resolvingAvail = new WeakSet(); // forms with a submit-time resolve in flight
  function fetchVariantAvailability(variantId) {
    const key = String(variantId);
    if (availPromises.has(key)) return availPromises.get(key);
    const baseUrl = document.querySelector("pickup-availability")?.dataset.baseUrl || "/";
    const p = fetch(`${baseUrl}variants/${key}/?section_id=pickup-availability`)
      .then((r) => {
        if (!r.ok) throw new Error(`pickup-availability section responded ${r.status}`);
        return r.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const m = new Map();
        doc.querySelectorAll("[data-pickup-store-item]").forEach((item) => {
          m.set(item.getAttribute("data-location-id"), item.getAttribute("data-available") === "true");
        });
        fetchedAvail.set(key, m);
        return m;
      });
    availPromises.set(key, p);
    p.catch(() => availPromises.delete(key)); // evict so a retry can succeed
    return p;
  }

  // true when the selected variant's availability isn't yet known: the
  // render-frozen JSON only carries a `stores` map for the ONE variant Shopify
  // rendered (variant.store_availabilities is empty for the others), and we
  // haven't live-fetched this variant. Callers must resolve it before deciding
  // rather than fail open (which would admit a sold-out-at-store variant).
  function needsLiveAvailability(form) {
    const vid = currentVariantId(form);
    if (!vid || fetchedAvail.has(String(vid))) return false;
    const vd = variantData(form);
    return !vd || !vd.stores || Object.keys(vd.stores).length === 0;
  }

  function availabilityAtStore(form, storeId) {
    if (!storeId) return null;
    // Authoritative for the rendered variant: the render-frozen JSON stores map.
    // Same rule as the live-fetched map below: store_availabilities only lists
    // locations that stock the variant, so a store MISSING from a non-empty map
    // isn't stocked there — returning null here would fail open and admit a
    // pickup line for a store that doesn't carry the item.
    const vd = variantData(form);
    if (vd && vd.stores && Object.keys(vd.stores).length) {
      const v = vd.stores[String(storeId)];
      return typeof v === "boolean" ? v : false;
    }
    // Non-rendered variant (empty JSON map) — use the live-fetched map if present.
    // A store absent from the section list means the variant isn't stocked there.
    const vid = currentVariantId(form);
    const fetched = vid ? fetchedAvail.get(String(vid)) : null;
    if (fetched) return fetched.has(String(storeId)) ? fetched.get(String(storeId)) : false;
    return null; // unknown -> caller resolves live before deciding
  }

  // Cart-state cache so the submit handler can read mode synchronously.
  let cartState = { itemCount: 0, pickupLineCount: 0, shipLineCount: 0, boundStoreName: null, boundStoreId: null };

  window.S2Pickup = window.S2Pickup || {};
  window.S2Pickup.getEffectiveStoreId = () =>
    getSelectedStore()?.id || cartState.boundStoreId || null;
  window.S2Pickup.isVariantAvailableAtStore = (variantId, storeId) => {
    if (!variantId || !storeId) return Promise.resolve(false);
    return fetchVariantAvailability(variantId)
      .then((map) => (map.has(String(storeId)) ? map.get(String(storeId)) === true : false))
      .catch(() => false);
  };
  let cartLoaded = false; // false until the first /cart.js resolves (see the cold-load hold)
  async function refreshCartState() {
    // A cart change (e.g. removing the conflicting item via the cart drawer) may
    // have resolved the mix conflict — drop any stale block warning so the gate
    // re-evaluates cleanly and the product becomes addable again.
    gateNotice = null;
    try {
      const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
      let pickup = 0;
      let ship = 0;
      for (const line of cart.items || []) {
        if (line.properties && line.properties._fulfillment === "pickup") pickup += line.quantity;
        else ship += line.quantity;
      }
      const attrs = cart.attributes || {};
      cartState = {
        itemCount: cart.item_count || 0,
        pickupLineCount: pickup,
        shipLineCount: ship,
        boundStoreName: attrs.pickup_store_name || null,
        boundStoreId: attrs.pickup_store_id || null, // survives cleared localStorage
      };
      cartLoaded = true; // only a SUCCESSFUL probe counts — deciding against the
      // all-zero initial state would skip the mix/bound-store guards, so a failed
      // fetch leaves the flag down and the cold-load hold retries on the next add.
      log("cart state", cartState);
      scheduleUi();
    } catch (e) {
      console.error("[pickup-atc-gate] cart state refresh failed", e);
    }
  }

  function ensureCard(form) {
    let card = form.querySelector("[data-pickup-guard-note]");
    if (!card) {
      card = document.createElement("div");
      card.setAttribute("data-pickup-guard-note", "");
      card.className = "pickup-store-card";
      card.innerHTML = [
        '<div class="pickup-store-card__row">',
        '<svg class="pickup-store-card__icon" width="14" height="18" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M4.75 8.75L6.75 10.75L10.75 6.75M14.75 7.95C14.75 11.9264 11.25 15.15 7.75 18.75C4.25 15.15 0.75 11.9264 0.75 7.95C0.75 3.97355 3.88401 0.75 7.75 0.75C11.616 0.75 14.75 3.97355 14.75 7.95Z" stroke="#FFFC00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        '<div class="pickup-store-card__body">',
        '<p class="pickup-store-card__text" data-pickup-card-text></p>',
        '<div class="pickup-store-card__actions" data-pickup-card-actions hidden>',
        '<button type="button" class="pickup-store-card__btn" data-pickup-change-store>Change store</button>',
        "</div>",
        '<div class="pickup-store-card__actions" data-pickup-notice-actions hidden>',
        '<button type="button" class="pickup-store-card__btn" data-pickup-dismiss-notice>Switch back to delivery</button>',
        "</div>",
        "</div>",
        "</div>",
      ].join("");
      (form.querySelector(".product-form__buttons") || form).appendChild(card);
    }
    return card;
  }
  // actions: "store" (Change store) | "notice" (mixed-cart CTAs) | null (none)
  function paintCard(form, { text, type = null, actions = null } = {}) {
    const card = ensureCard(form);
    const textEl = card.querySelector("[data-pickup-card-text]");
    const storeActions = card.querySelector("[data-pickup-card-actions]");
    const noticeActions = card.querySelector("[data-pickup-notice-actions]");
    const t = text || "";
    if (textEl.textContent !== t) textEl.textContent = t;
    textEl.hidden = !t;
    storeActions.hidden = actions !== "store";
    noticeActions.hidden = actions !== "notice";
    card.classList.toggle("pickup-store-card--error", type === "error");
    card.classList.toggle("pickup-store-card--ok", type === "ok");
    card.hidden = !t && !actions;
  }

  // Returns true if the drawer was opened. Prefer Dawn's rendered opener (so its
  // show() + focus-trap fire); fall back to the drawer element directly when the
  // opener hasn't been injected yet — mirrors the selector's openStoreDrawer().
  function openDrawer() {
    const opener = document.getElementById("ShowPickupAvailabilityDrawer");
    const drawer = document.querySelector("pickup-availability-drawer");
    const toggle = document.querySelector('[data-pickup-mode="pickup"]');
    if (opener) {
      log("opening pickup drawer for store selection");
      opener.click();
      if (drawer && toggle) drawer.focusElement = toggle;
      return true;
    }
    if (drawer && typeof drawer.show === "function") {
      log("opening pickup drawer directly (opener not injected yet)");
      drawer.show();
      if (toggle) drawer.focusElement = toggle;
      return true;
    }
    log("WARN: pickup drawer not available — cannot open");
    return false;
  }

  function stampFulfillment(form, isPickup) {
    let input = form.querySelector('input[name="properties[_fulfillment]"]');
    if (isPickup) {
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "properties[_fulfillment]";
        form.appendChild(input);
      }
      input.value = "pickup";
    } else if (input) {
      input.remove();
    }
  }

  // Sticky gate warning (the blocked-add message). refreshUi() still runs on cart
  // updates, variant changes, and in-form repaints — any of which would otherwise
  // wipe the message before the shopper can read it. We keep it visible until they
  // act (dismiss / re-submit / store or cart change) or navigate away; refreshUi
  // honors it (see below).
  let gateNotice = null; // { form, message } | null — scoped to the form it was raised on
  function block(e, form, message) {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (message) {
      gateNotice = { form, message };
      paintCard(form, { text: message, type: "error", actions: "notice" });
    }
  }

  // Capture-phase submit guard so we beat Dawn's product-form handler.
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target.closest(FORM_SELECTOR);
      if (!form) return;

      // Gating reads cart mode from an async /cart.js. On a cold load (fast click
      // before it resolves) cartState is all-zero, which would let an exempt/ship
      // item slip into a pickup-bound cart and skip the bound-store check. Hold
      // the first add until the cart is known, then re-submit.
      if (!cartLoaded) {
        block(e, form, null);
        if (!resolvingAvail.has(form)) {
          resolvingAvail.add(form);
          refreshCartState().finally(() => {
            resolvingAvail.delete(form);
            if (cartLoaded) {
              form.requestSubmit();
            } else {
              // /cart.js failed — re-submitting would decide against all-zero
              // state and fail open. Surface a retryable error; the next click
              // re-attempts the probe (cartLoaded is still false).
              block(e, form, "Couldn't load your cart to verify this add. Please try again.");
            }
          });
        }
        return;
      }

      const data = getFormData(form);
      if (data && data.exempt) {
        stampFulfillment(form, false);
        // Exempt items are always ship lines — they can't join a pickup-bound
        // cart (backend rejects mixed carts at checkout), and a store-bound but
        // pickup-empty cart must not silently become a ship cart still carrying
        // pickup_store_* attrs (checkout's location filter would misread it).
        // The notice's "Switch back to delivery" button clears the binding.
        const exemptSelected = getSelectedStore();
        const exemptBound = cartState.pickupLineCount > 0 || exemptSelected || cartState.boundStoreId;
        if (exemptBound) {
          const boundName = exemptSelected?.name || cartState.boundStoreName || "your store";
          block(
            e,
            form,
            cartState.pickupLineCount > 0
              ? `Your cart is set to in-store pickup at ${boundName}. This item can't be added to a pickup order — remove your pickup items or switch back to delivery first.`
              : `You're set for in-store pickup at ${boundName}, but this item is delivery-only — switch back to delivery to add it.`,
          );
        }
        return;
      }

      gateNotice = null;

      const pickupOnly = isPickupOnly(form);
      const selectedStore = getSelectedStore();
      const c = cartState;
      // The bound store may live only on the cart (localStorage cleared / new
      // device / a failed resync). Fall back to the cart's attr id + name so we
      // still check availability instead of failing open with no store id.
      const effStoreId = selectedStore?.id || c.boundStoreId || null;
      const effStoreName = selectedStore?.name || c.boundStoreName || "your store";

      // A non-rendered variant has no availability in the render-frozen JSON
      // (Shopify only fills variant.store_availabilities for the rendered
      // variant). If a store is in play and we can't yet tell whether THIS
      // variant is stocked there, resolve it live and re-decide — never fail
      // open, or a sold-out-at-store variant would be added as a pickup line.
      const storeInPlay = c.pickupLineCount > 0 || !!selectedStore;
      if (storeInPlay && effStoreId && needsLiveAvailability(form)) {
        block(e, form, null); // hold the add until availability is known
        if (resolvingAvail.has(form)) return; // a resolve is already in flight
        resolvingAvail.add(form);
        fetchVariantAvailability(currentVariantId(form))
          .then(() => {
            scheduleUi();
            // Release the in-flight guard BEFORE re-submitting: if the shopper
            // switched variants mid-resolve, the re-entered submit needs its own
            // resolve — a stale guard would silently swallow that click.
            resolvingAvail.delete(form);
            if (availabilityAtStore(form, effStoreId) === false) {
              block(e, form, `Sold out at ${effStoreName}. Open the store selector to switch stores or shop for delivery.`);
            } else {
              form.requestSubmit(); // re-enter: availability now cached -> normal path
            }
          })
          .catch(() => {
            resolvingAvail.delete(form);
            block(e, form, `Couldn't check availability at ${effStoreName}. Please try again.`);
          });
        return;
      }

      if (c.pickupLineCount > 0) {
        if (availabilityAtStore(form, effStoreId) !== false) {
          stampFulfillment(form, true);
          return;
        }
        block(e, form, `Your cart is set to in-store pickup only at ${effStoreName}. This item isn't available there — remove your pickup items or choose a different store.`);
        return;
      }

      if (c.shipLineCount > 0) {
        if (pickupOnly) {
          block(e, form, "Your cart has shippable items. Remove them to switch this item to in-store pickup.");
          return;
        }
        stampFulfillment(form, false);
        return;
      }

      if (effStoreId && availabilityAtStore(form, effStoreId) === false) {
        block(
          e,
          form,
          `Sold out at ${effStoreName}. Open the store selector to switch stores or shop for delivery.`,
        );
        return;
      }
      if (pickupOnly) {
        if (selectedStore) {
          stampFulfillment(form, true);
          return;
        }
        if (openDrawer()) {
          block(e, form, null);
        } else if (data.full) {
          // On the PDP itself with no drawer to open — no store to send them to.
          block(e, form, "This item is in-store pickup only and isn't currently available for pickup.");
        } else {
          block(e, form, "This item is in-store pickup only. Open the product page to choose a store.");
        }
        return;
      }
      stampFulfillment(form, !!selectedStore);
    },
    true,
  );

  function refreshUi() {
    const boundStore = cartState.pickupLineCount > 0 ? cartState.boundStoreName : null;
    const savedStore = getSelectedStore();
    // Paint from the SAME store the submit path gates on (effStoreId there):
    // localStorage first, else the cart's attrs — otherwise a cleared-storage
    // shopper sees an enabled "Add to Cart" whose every click blocks.
    const effStore =
      savedStore ||
      (cartState.boundStoreId
        ? { id: cartState.boundStoreId, name: cartState.boundStoreName || "your store" }
        : null);
    const cartEmpty = cartState.itemCount === 0;

    document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      const data = getFormData(form);
      if (!data) return;

      // Buy-It-Now (dynamic checkout) fires no submit, so the gate can't
      // stamp/block it and Dawn never re-renders it on variant change. Hide it
      // whenever the SELECTED variant is pickup-only (it can't ship) — this
      // covers delivery mode and mixed-variant products the Liquid guard (which
      // only reads the first variant) misses.
      form.classList.toggle("s2-variant-pickup-only", isPickupOnly(form));

      // Warm the live availability cache for non-rendered variants so the card,
      // label, and submit reflect real store stock without a click round-trip.
      if (effStore?.id && needsLiveAvailability(form)) {
        fetchVariantAvailability(currentVariantId(form)).then(scheduleUi).catch(() => {});
      }

      const noticeHere = gateNotice && gateNotice.form === form ? gateNotice.message : null;

      // Exempt / non-full forms (product cards, featured sections, quickview) have
      // no store-status card UI — but a block() notice CAN land on them (mixed-cart
      // or pickup-only add). Keep that notice paintable AND clearable here; never
      // inject a fresh card into a form that never raised one.
      if (data.exempt || !data.full) {
        if (noticeHere) {
          paintCard(form, { text: noticeHere, type: "error", actions: "notice" });
        } else if (form.querySelector("[data-pickup-guard-note]")) {
          paintCard(form, { text: "", type: null, actions: null });
        }
        return;
      }

      const label = form.querySelector(`${SUBMIT_SELECTOR} ${LABEL_SELECTOR}`);

      if (noticeHere) {
        paintCard(form, { text: noticeHere, type: "error", actions: "notice" });
      } else if (boundStore) {
        const availHere = availabilityAtStore(form, effStore?.id) !== false;
        paintCard(form, {
          text: availHere
            ? `Picking up at ${boundStore}`
            : `Not available at ${boundStore} — your cart is set to pickup there.`,
          type: availHere ? "ok" : "error",
          actions: "store",
        });
      } else if (effStore && cartEmpty) {
        paintCard(form, {
          text: `Pickup store: ${effStore.name}`,
          type: null,
          actions: "store",
        });
      } else {
        paintCard(form, { text: "", type: null, actions: null });
      }

      const submitBtn = form.querySelector(SUBMIT_SELECTOR);
      const storeBlocked = !!(effStore && availabilityAtStore(form, effStore.id) === false);
      const storeAvailable = !!(effStore && !storeBlocked);
      if (label && !(submitBtn && submitBtn.hasAttribute("disabled"))) {
        if (!label.hasAttribute("data-s2-orig-label")) {
          label.setAttribute("data-s2-orig-label", label.textContent.trim());
        }
        const orig = label.getAttribute("data-s2-orig-label");
        let next;
        if (storeBlocked) next = `Sold Out at ${effStore.name}`;
        else if (storeAvailable) next = `Add to Cart — ${effStore.name} selected`;
        else next = orig;
        if (next != null && label.textContent.trim() !== next) label.textContent = next;
      }
      if (submitBtn) {
        submitBtn.classList.toggle("pickup-atc--unavailable", storeBlocked);
        submitBtn.setAttribute("aria-disabled", storeBlocked ? "true" : "false");
      }
    });
  }

  let uiQueued = false;
  function scheduleUi() {
    if (uiQueued) return;
    uiQueued = true;
    // setTimeout (not rAF) — headless Playwright doesn't fire rAF reliably.
    setTimeout(() => {
      uiQueued = false;
      refreshUi();
    }, 50);
  }

  // Store-status card actions: "Change store" reopens the drawer;
  // "Switch back to delivery" clears the store binding when one exists (via the
  // selector's pickup:request-clear listener), otherwise just dismisses. (The
  // Delivery toggle — the other cart-clear path — lives in pickup-store-selector.js.)
  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-pickup-change-store]")) {
      e.preventDefault();
      openDrawer();
      return;
    }
    // "Switch back to delivery": when pickup state is in play, run the selector's
    // real clear path (empties the pickup cart + drops the store binding) — works
    // on exempt PDPs too, where the Delivery toggle is suppressed and this button
    // is the only affordance. With NO pickup state (e.g. the "cart has shippable
    // items" notice over a pure delivery cart) it's a plain dismiss: requesting a
    // clear there would wipe the shopper's delivery cart for nothing.
    if (e.target.closest("[data-pickup-dismiss-notice]")) {
      e.preventDefault();
      gateNotice = null;
      if (getSelectedStore() || cartState.pickupLineCount > 0 || cartState.boundStoreId) {
        document.dispatchEvent(new CustomEvent("pickup:request-clear"));
      }
      refreshUi();
    }
  });

  // Variant switch clears a stale blocked-add notice. Dawn dispatches a bubbling
  // `change` on input[name="id"] from updateVariantInput (global.js) on every
  // switch, so a "Sold out at StoreA" notice can't outlive a switch to a variant
  // that IS available (which would otherwise contradict the recomputed ATC label).
  document.addEventListener("change", (e) => {
    if (e.target && e.target.name === "id") {
      gateNotice = null; // a stale "Sold out at StoreA" can't outlive a switch
      scheduleUi(); // recompute BIN visibility + availability for the new variant
    }
  });

  // Store selected/cleared: refreshCartState() already nulls gateNotice at its top
  // and schedules a UI pass, so both events just re-read the cart.
  ["pickup:store-selected", "pickup:store-cleared"].forEach((evt) =>
    document.addEventListener(evt, () => refreshCartState()),
  );

  try {
    // Dawn declares PUB_SUB_EVENTS as a bare top-level `const` (global.js), NOT on
    // window — so a `window.PUB_SUB_EVENTS` guard never matches and the gate would
    // silently miss in-session cart changes (add/remove without a store change).
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartState);
    }
  } catch (_) {}

  // Repaint triggers the explicit listeners above can't see:
  //  1. the pickup drawer/opener mounting (pickup-availability.js appends them
  //     after an async fetch);
  //  2. a whole ATC form being injected (quickview modal clones MainProduct —
  //     its form needs the BIN class / card / label painted on arrival);
  //  3. Dawn's renderProductInfo swapping the submit button's innerHTML after
  //     its section fetch (~300ms post variant change) — it restores the default
  //     enabled label AFTER our change-listener repaint already ran.
  // Still ignores unrelated churn (lazy-load, carousels, third-party widgets):
  // only drawer inserts, form inserts, and mutations INSIDE an ATC form fire.
  // Our own card/label writes are change-only (paintCard + the label guard), so
  // a gate-originated mutation costs at most one extra no-op refreshUi pass.
  const gateObserver = new MutationObserver((records) => {
    const sel = "pickup-availability-drawer, #ShowPickupAvailabilityDrawer";
    for (const rec of records) {
      if (rec.target instanceof Element && rec.target.closest(FORM_SELECTOR)) {
        scheduleUi();
        return;
      }
      for (const node of rec.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (
          node.matches?.(sel) ||
          node.querySelector?.(sel) ||
          node.matches?.(FORM_SELECTOR) ||
          node.querySelector?.(FORM_SELECTOR)
        ) {
          scheduleUi();
          return;
        }
      }
    }
  });
  gateObserver.observe(document.body, { childList: true, subtree: true });

  // Only probe the cart where there's something to gate/paint — skip the fetch on
  // pure content pages (blog, policies) with no ATC form or pickup UI. If a form
  // appears later (quickview), the cold-load hold fetches on the first add.
  if (document.querySelector(FORM_SELECTOR) || document.querySelector("pickup-availability")) {
    refreshCartState();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", refreshUi);
  else refreshUi();
  log("initialized");
})();
